
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { useInterval } from "@/hooks/use-interval";
import { useCarouselImages } from "@/hooks/useCarouselImages";

// Fallback images in case database is empty
const fallbackImages = [
  {
    url: "https://images.unsplash.com/photo-1600618528240-fb9fc964b853?q=80&w=1200&auto=format&fit=crop",
    alt: "Serene meditation space representing spiritual healing"
  },
  {
    url: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?q=80&w=1200&auto=format&fit=crop",
    alt: "Crystal healing therapy session"
  },
  {
    url: "https://images.unsplash.com/photo-1593811167562-9cef47bfc4d7?q=80&w=1200&auto=format&fit=crop",
    alt: "Spiritual guidance and energy work"
  },
  {
    url: "https://images.unsplash.com/photo-1529693662653-9d480530a697?q=80&w=1200&auto=format&fit=crop",
    alt: "Peaceful nature setting for spiritual connection"
  }
];

const HeroSection = () => {
  const { user, activeRole } = useAuth();
  const { t } = useTranslation('homepage');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const { images: carouselImages, loading } = useCarouselImages();

  // Use carousel images from database or fallback to hardcoded ones
  const images = carouselImages.length > 0 
    ? carouselImages.map(img => ({ url: img.file_path, alt: img.alt_text }))
    : fallbackImages;

  // Auto advance carousel every 8 seconds
  useInterval(() => {
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
  }, 8000);

  return (
    <section className="relative py-16 md:py-24 overflow-hidden hero-gradient">
      <div className="container mx-auto px-4 relative z-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div className="space-y-6 md:pr-8 order-2 md:order-1">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-medium leading-tight text-earth-900">
              {t('heroTitle')}
            </h1>
            <p className="text-lg md:text-xl text-earth-700 leading-relaxed">
              {t('heroSubtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 pt-2">
              <Button asChild size="lg" className="bg-amber-600 hover:bg-amber-700 text-white font-semibold">
                <Link to="/booking-choice">
                  {t('bookAppointment', { ns: 'navigation' })} <ArrowRight size={16} className="ml-2" />
                </Link>
              </Button>
              {user ? (
                <Button asChild size="lg" variant="outline" className="border-amber-500 text-amber-700 hover:bg-amber-50">
                  <Link to={activeRole === 'client' ? '/client-dashboard' : '/practitioner-dashboard'}>
                    {t('myDashboard', { ns: 'navigation' })}
                  </Link>
                </Button>
              ) : (
                <Button asChild size="lg" variant="outline" className="border-amber-500 text-amber-700 hover:bg-amber-50">
                  <Link to="/become-practitioner">
                    {t('joinAsPractitioner')}
                  </Link>
                </Button>
              )}
            </div>
          </div>
          <div className="relative h-[300px] md:h-[400px] lg:h-[500px] rounded-lg overflow-hidden shadow-xl order-1 md:order-2">
            <div className="absolute inset-0 bg-gradient-to-r from-amber-400/20 to-amber-100/20 mix-blend-overlay rounded-lg z-10"></div>
            
            {/* Image Carousel */}
            <div className="relative w-full h-full">
              {!loading && images.map((image, index) => (
                <div 
                  key={index}
                  className={`absolute inset-0 transition-opacity duration-1000 ${
                    index === currentImageIndex ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <img 
                    src={image.url} 
                    alt={image.alt} 
                    className="w-full h-full object-cover rounded-lg"
                    loading={index === 0 ? "eager" : "lazy"}
                    crossOrigin="anonymous"
                    sizes="(max-width: 768px) 100vw, 50vw"
                    fetchPriority={index === 0 ? "high" : "low"}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
